/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Theme Composable - Single Source of Truth for Dark/Light Mode + Light Variant
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
// 💬 Why a composable (not a Pinia store):
//   • Theme state is tied to the DOM (class on <html>) and to localStorage,
//     not to anything that needs cross-tab reactive sharing or hydration
//     from Firestore. A simple shared ref + side-effects is the right shape.
//   • The previous logic lived inside ThemeToggle.vue. That worked when only
//     one component cared about the theme — now the Appearance settings page
//     also needs to read/write it, so we centralize.
//
// 💬 Design:
//   • `themeMode`        → 'dark' | 'light'   (persisted as `localStorage.theme`)
//   • `lightVariant`     → one of 6 named variants (persisted as `localStorage.lightTheme`)
//   • Class application: <html> always carries either `dark` OR a `light-*`
//     class (mutually exclusive). Existing `dark:` Tailwind selectors keep
//     working unchanged; light variants drive CSS variables in main.css.
//   • Defaults: dark mode + Pearl Studio (the cleanest variant — see
//     theme-options-preview.html for the visual reference).

export type LightVariant = 'pearl' | 'aurora' | 'glacier' | 'twilight' | 'sage' | 'latte'

export const LIGHT_VARIANTS: ReadonlyArray<{
  id: LightVariant
  name: string
  blurb: string
  swatches: string[]
}> = [
  {
    id: 'pearl',
    name: 'Pearl Studio',
    blurb: 'Pure SaaS minimalism — off-white surfaces, hairline borders, brand purple in accents only.',
    swatches: ['#FAFAFA', '#FFFFFF', '#EDEDED', '#6366F1', '#18181B'],
  },
  {
    id: 'aurora',
    name: 'Aurora Dawn',
    blurb: 'Soft lavender-peach-sky gradient backdrop with crisp frosted-white panels. Sunrise counterpart to the nebula.',
    swatches: ['#FBF5FF', '#FFE9DC', '#E0F0FF', '#A78BFA', '#F472B6'],
  },
  {
    id: 'glacier',
    name: 'Glacier',
    blurb: 'Dark navy sidebar (matches dark mode) + cool icy blue-white workspace. Strongest cohesion with dark theme.',
    swatches: ['#1E2A47', '#EAF1F8', '#FFFFFF', '#3B82F6', '#06B6D4'],
  },
  {
    id: 'twilight',
    name: 'Twilight',
    blurb: 'Light reimagining of the nebula — subtle pink + indigo radial glows, dark navy sidebar shared with dark mode.',
    swatches: ['#1A1530', '#F4F1FB', '#E9E6F7', '#A78BFA', '#F0ABFC'],
  },
  {
    id: 'sage',
    name: 'Sage Editorial',
    blurb: 'Warm cream paper background, deep forest sidebar, sage green and terracotta accents. Calm and editorial.',
    swatches: ['#2D3A2E', '#FAF8F2', '#84A98C', '#D6A77A', '#52796F'],
  },
  {
    id: 'latte',
    name: 'Cosmic Latte',
    blurb: 'Warm ivory base (the averaged color of starlight) with amber and pink accents. Distinctive and inviting.',
    swatches: ['#FFF8E7', '#FFEEDD', '#FFFFFF', '#F59E0B', '#EC4899'],
  },
] as const

const VALID_VARIANTS = new Set<LightVariant>(LIGHT_VARIANTS.map(v => v.id))

const DEFAULT_VARIANT: LightVariant = 'pearl'

// 💬 Module-scoped refs so every consumer sees the same state.
// (Composables in Nuxt instantiate per-call; we want shared state.)
const themeMode = ref<'dark' | 'light'>('dark')
const lightVariant = ref<LightVariant>(DEFAULT_VARIANT)
let initialized = false

const applyToDom = () => {
  if (typeof document === 'undefined') return
  const html = document.documentElement

  // Drop any prior light variant class — they're mutually exclusive.
  for (const v of VALID_VARIANTS) html.classList.remove(`light-${v}`)

  if (themeMode.value === 'dark') {
    html.classList.add('dark')
  } else {
    html.classList.remove('dark')
    html.classList.add(`light-${lightVariant.value}`)
  }
}

const persist = () => {
  if (typeof localStorage === 'undefined') return
  localStorage.theme = themeMode.value
  localStorage.lightTheme = lightVariant.value
}

const init = () => {
  if (initialized) return
  initialized = true
  if (typeof localStorage === 'undefined') return

  const storedMode = localStorage.theme
  themeMode.value = storedMode === 'light' ? 'light' : 'dark' // dark-first default

  const storedVariant = localStorage.lightTheme as LightVariant | undefined
  lightVariant.value = storedVariant && VALID_VARIANTS.has(storedVariant)
    ? storedVariant
    : DEFAULT_VARIANT

  applyToDom()
}

export const useTheme = () => {
  // Idempotent init. Safe to call from many components.
  if (typeof window !== 'undefined') init()

  const setMode = (mode: 'dark' | 'light') => {
    themeMode.value = mode
    applyToDom()
    persist()
  }

  const toggleMode = () => setMode(themeMode.value === 'dark' ? 'light' : 'dark')

  const setLightVariant = (variant: LightVariant) => {
    if (!VALID_VARIANTS.has(variant)) return
    lightVariant.value = variant
    applyToDom()
    persist()
  }

  const isDark = computed(() => themeMode.value === 'dark')

  return {
    themeMode: readonly(themeMode),
    lightVariant: readonly(lightVariant),
    isDark,
    variants: LIGHT_VARIANTS,
    defaultVariant: DEFAULT_VARIANT,
    setMode,
    toggleMode,
    setLightVariant,
  }
}
